/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unnecessary-type-assertion, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any, @typescript-eslint/strict-boolean-expressions */

import { NavLink } from 'react-router-dom';
import ListItemText from '@mui/material/ListItemText';
import {
  Box,
  Table,
  TableBody,
  TableCell as MuiTableCell,
  TableHead,
  TableRow,
  Typography,
  Stack,
} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { styled, useTheme } from '@mui/material/styles';
import useIsMobile from 'hooks/useIsMobile';

import {
  businessDaysAdd,
  businessDaysSubtract,
  dayjs,
  formatAsPercentage,
  getDateFormatted,
  getQueryDate,
} from '../../util';
import { useRecoilValue } from 'recoil';
import {
  timezoneState,
  hiroSymbolsState,
  earningsCalendarState,
} from '../../states';
import { ProductType } from '../../types';
import { useState, useEffect } from 'react';
import { Loader } from 'components/shared';
import { Center } from 'components/shared/Center';
import { EARNINGS_PERIOD_LABEL } from '../../config/home';
import useAuth from 'hooks/auth/useAuth';

const TableCell = styled(MuiTableCell)({
  borderBottom: 'none',
  padding: 0,
});

const EarningsList = ({ earnings }: { earnings: any[] }) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const hiroSyms = useRecoilValue(hiroSymbolsState);
  const { hasAccessToProduct } = useAuth();
  const hasHiroAccess = hasAccessToProduct(ProductType.HIRO);
  const fontSize = isMobile ? 12 : 14;
  const currentTimezone = useRecoilValue(timezoneState);

  const renderRow = (data: any, key: string) => {
    const etTime = dayjs.utc(data.utc).tz(currentTimezone);
    const inHiro = hasHiroAccess && hiroSyms.has(data.sym);

    return (
      <TableRow key={key}>
        <TableCell>
          <ListItemText
            primary={
              <NavLink
                to={
                  inHiro
                    ? `/${ProductType.HIRO}?sym=${data.sym}`
                    : `/${ProductType.EQUITYHUB}?sym=${data.sym}`
                }
                style={{ textDecoration: 'none' }}
              >
                <Typography color="primary" sx={{ fontSize: fontSize }}>
                  {data.sym}
                </Typography>
              </NavLink>
            }
            secondary={data.company_name}
          />
        </TableCell>
        <TableCell align="right">
          <Typography
            sx={{
              fontSize: fontSize,
              marginRight: '10px',
              textAlign: 'right',
            }}
            color={theme.palette.text.secondary}
          >
            {data.implied_move && formatAsPercentage(data.implied_move)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography
            sx={{
              fontSize: fontSize,
              marginRight: '10px',
              textAlign: 'right',
            }}
            color={theme.palette.text.secondary}
          >
            {`${EARNINGS_PERIOD_LABEL.get(data.period) ?? ''}: `}
            {etTime.format('ddd, h:mm[\u00a0]A[\u00a0]z')}
          </Typography>
        </TableCell>
      </TableRow>
    );
  };

  if (earnings == null || earnings.length === 0) {
    return <Typography>No earnings scheduled.</Typography>;
  }

  return (
    <Box sx={{ overflow: 'auto' }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell width="150px" component="th">
              Name
            </TableCell>
            <TableCell component="th" align="right">
              Implied Move
            </TableCell>
            <TableCell component="th" align="center">
              Time
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {earnings
            .filter((e) => e.confirmed)
            .map((elem, idx: number) => renderRow(elem, `${idx}`))}
        </TableBody>
      </Table>
    </Box>
  );
};

export const EarningsDay = ({
  day,
  earnings,
  onExpanded,
  expanded,
}: {
  day: dayjs.Dayjs;
  earnings: any[];
  onExpanded: (day: dayjs.Dayjs | undefined) => void;
  expanded: boolean;
}) => {
  return (
    <Accordion
      key={day.toString()}
      defaultExpanded={expanded}
      sx={{ margin: '0px !important' }}
      onChange={(_evt, expanded: boolean) =>
        expanded ? onExpanded(day) : onExpanded(undefined)
      }
    >
      <AccordionSummary
        sx={{
          height: '40px',
          minHeight: '40px',
        }}
        expandIcon={<ExpandMoreIcon color="primary" />}
      >
        <Typography>
          <h4 style={{ margin: '8px 0px 10px 0px' }}>
            {day == null
              ? `Upcoming In Your Watchlist`
              : `Earnings for ${day.format('dddd, MMMM DD, YYYY')}`}
          </h4>
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {
          // Only render what's visible
          expanded && <EarningsList earnings={earnings} />
        }
      </AccordionDetails>
    </Accordion>
  );
};

export const EarningsCalendar = () => {
  const today = getQueryDate(true);
  const [expandedDay, setExpandedDay] = useState<dayjs.Dayjs | undefined>(
    today,
  );

  const earningsCalendar = useRecoilValue<any>(earningsCalendarState);

  const [loading, setLoading] = useState<boolean>(false);
  const [errored, setErrored] = useState<boolean>(false);

  useEffect(() => {
    // undefined => not loaded yet, null => there was an error
    setLoading(earningsCalendar === undefined);
    setErrored(earningsCalendar === null);
  }, [earningsCalendar]);

  if (earningsCalendar == null) {
    return null;
  }

  const datesToDisplay = [
    businessDaysSubtract(today, 1),
    today,
    businessDaysAdd(today, 1),
    businessDaysAdd(today, 2),
    businessDaysAdd(today, 3),
    businessDaysAdd(today, 4),
    businessDaysAdd(today, 5),
  ];

  // TODO: Add 'upcoming in your watchlist' section
  return (
    <Loader isLoading={loading}>
      {errored ? (
        <Center
          sx={{
            cursor: 'pointer',
          }}
        >
          <Typography sx={{ fontSize: '14px' }}>
            Unfortunately, there was an error.
          </Typography>
        </Center>
      ) : (
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Stack sx={{ width: '100%' }}>
            {datesToDisplay.map((date: dayjs.Dayjs, idx: number) => (
              <EarningsDay
                key={`${idx}`}
                day={date}
                earnings={
                  earningsCalendar.get(getDateFormatted(date!)) as any[]
                }
                onExpanded={(day) => setExpandedDay(day)}
                expanded={expandedDay?.isSame(date, 'day') ?? false}
              />
            ))}
          </Stack>
        </Stack>
      )}
    </Loader>
  );
};
